import { Text } from "@dropbox/dig-components/typography";
import { findHighlightableChunksInQuery } from "@mirage/search/SearchResults/util/resultUtil";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { useResizeObserver } from "@react-hookz/web";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { useRef, useState } from "react";
import ReactHighlightWords from "react-highlight-words";
import styles from "./TitleHighlighter.module.css";

type DIGTextSize = "xlarge" | "large" | "medium" | "small" | "xsmall";

type TitleHighlighterProps = Readonly<{
  title: string;
  query?: string;
  textSize?: DIGTextSize;
}>;

const HighlightTag = (textSize: DIGTextSize) => {
  const Inner: React.FC = ({ children }) => (
    <Text size={textSize} tagName="span" isBold={true}>
      {children}
    </Text>
  );
  return Inner;
};

export function TitleHighlighter({
  title = "",
  query = "",
  textSize = "medium",
}: TitleHighlighterProps) {
  const isMobileSize = useIsMobileSize();

  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const shouldShowTooltip = isMobileSize || isTouchDevice;

  const [findChunks, searchWords] = findHighlightableChunksInQuery(query);
  const titleRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [tooltipHover, setTooltipHover] = useState(false);
  const [titleHover, setTitleHover] = useState(false);

  const handleResize = debounce(() => {
    if (!titleRef.current) return;
    const { scrollWidth, clientWidth } = titleRef.current;
    setIsOverflowing(scrollWidth > clientWidth);
  }, 100);

  useResizeObserver(titleRef, handleResize, shouldShowTooltip);

  return (
    <>
      {isOverflowing && (
        <DigTooltip.Control
          open={tooltipHover || titleHover}
          triggerRef={titleRef}
          placement="bottom-start"
          openDelay={100}
          className={styles.tooltipContent}
          onMouseEnter={() => setTooltipHover(true)}
          onMouseLeave={() => setTooltipHover(false)}
          style={{ background: "transparent" }}
        >
          <Text size="small" tagName="span">
            {title}
          </Text>
        </DigTooltip.Control>
      )}
      <div
        ref={titleRef}
        onMouseEnter={() => setTitleHover(true)}
        onMouseLeave={() => setTitleHover(false)}
        className={classNames(styles.container, {
          [styles.fadeout]: isOverflowing,
        })}
      >
        <ReactHighlightWords
          className={styles.titleHighlighter}
          searchWords={searchWords}
          autoEscape={true}
          textToHighlight={title}
          caseSensitive={false}
          highlightTag={HighlightTag(textSize)}
          findChunks={findChunks}
        />
      </div>
    </>
  );
}
